import { useState, useEffect } from 'react';

const DISMISSED_KEY = 'sv_install_dismissed';

function isIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.MSStream
  );
}

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid,    setShowAndroid]    = useState(false);
  const [showIOS,        setShowIOS]        = useState(false);

  useEffect(() => {
    // Don't show if already installed or previously dismissed
    if (isInStandaloneMode()) return;
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    if (isIOS()) {
      // iOS doesn't fire beforeinstallprompt — show manual instructions
      setShowIOS(true);
      return;
    }

    // Android / Chrome / Edge — listen for the native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroid(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setShowAndroid(false);
    setShowIOS(false);
  }

  async function installAndroid() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowAndroid(false);
    }
    setDeferredPrompt(null);
  }

  if (!showAndroid && !showIOS) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4">
      <div className="max-w-lg mx-auto bg-gray-900 border border-brand-500/40 rounded-2xl shadow-2xl shadow-black/50 p-4 flex items-start gap-4">
        {/* Icon */}
        <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-500/20 flex items-center justify-center text-brand-400 text-xl">
          ⬡
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm leading-snug">
            Install Snapvault on your {showIOS ? 'iPhone / iPad' : 'device'}
          </p>

          {showIOS ? (
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              Tap the{' '}
              <span className="inline-flex items-center gap-0.5 text-brand-300 font-medium">
                <ShareIcon /> Share
              </span>
              {' '}button, then choose{' '}
              <strong className="text-gray-200">Add to Home Screen</strong>
            </p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              Add to your home screen for quick access — works offline too.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {showAndroid && (
            <button
              onClick={installAndroid}
              className="px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold transition-colors"
            >
              Install
            </button>
          )}
          <button
            onClick={dismiss}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg className="w-3.5 h-3.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  );
}
